/**
 * Lista opcji dostawy w bocznym panelu koszyka.
 *
 * Blok koszyka WooCommerce pokazuje tylko szacunkowy koszt wysyłki — wybór
 * metody odbywa się dopiero na Kasie. Ten komponent renderuje stawki jako
 * radia już w koszyku i wpina się w store WooCommerce (wp.data → wc/store/cart),
 * więc zaznaczenie od razu przelicza podsumowanie.
 */

const STORE = 'wc/store/cart'
const TARGET = '.wp-block-woocommerce-cart-order-summary-shipping-block'
const FALLBACK_TARGET = '.wc-block-cart__totals-title'

// Opcja „koszt ustalany indywidualnie” ma cenę 0 zł — nie pokazujemy przy niej
// kwoty, żeby nie sugerować darmowej dostawy.
const NO_PRICE_SUFFIX = ':indywidualnie'

// Klasa na <body>, gdy wybrano opcję o koszcie do ustalenia. Podmiana etykiety
// „Bezpłatnie” w podsumowaniu idzie przez CSS, bo ten wiersz renderuje React
// i każda zmiana w DOM zostałaby nadpisana przy przerysowaniu.
const TBD_CLASS = 'opal-shipping-tbd'

export default class CartShipping {
  constructor() {
    this.isCart = document.body.classList.contains('woocommerce-cart')
    this.isCheckout = document.body.classList.contains('woocommerce-checkout')

    if (!this.isCart && !this.isCheckout) return

    this.signature = ''
    this.container = null

    this.start()
  }

  async start() {
    const ready = await this.waitFor(
      () => window.wp?.data?.select?.(STORE)?.getCartData
    )
    if (!ready) return

    wp.data.subscribe(() => this.render())
    this.render()
  }

  waitFor(check, timeout = 15000, interval = 100) {
    return new Promise((resolve) => {
      const started = Date.now()
      const tick = () => {
        try {
          if (check()) return resolve(true)
        } catch (e) {
          /* store jeszcze niegotowy */
        }
        if (Date.now() - started > timeout) return resolve(false)
        setTimeout(tick, interval)
      }
      tick()
    })
  }

  getCart() {
    return wp.data.select(STORE).getCartData()
  }

  render() {
    const cart = this.getCart()
    if (!cart) return

    const packages = cart.shippingRates || []

    this.syncTbdState(packages)

    // Selektor stawek dokładamy tylko w koszyku — Kasa ma już własny.
    if (!this.isCart) return

    if (!cart.needsShipping || !packages.length) {
      this.destroy()
      return
    }

    // Przerysowujemy tylko, gdy faktycznie zmieniły się stawki lub zaznaczenie.
    const signature = JSON.stringify(
      packages.map((pkg) => [
        pkg.package_id,
        (pkg.shipping_rates || []).map((r) => [r.rate_id, r.price, r.selected]),
      ])
    )

    if (signature === this.signature && this.container?.isConnected) return
    this.signature = signature

    this.mount(packages)
  }

  mount(packages) {
    const anchor = document.querySelector(TARGET)
    const fallback = document.querySelector(FALLBACK_TARGET)
    if (!anchor && !fallback) return

    this.destroy()

    this.container = document.createElement('div')
    this.container.className = 'opal-shipping-options'
    this.container.innerHTML = `
      <h3 class="opal-shipping-options__title">Opcje dostawy</h3>
      ${packages.map((pkg) => this.renderPackage(pkg)).join('')}
    `

    this.container.addEventListener('change', (e) => this.onChange(e))

    if (anchor) {
      anchor.parentNode.insertBefore(this.container, anchor)
    } else {
      fallback.insertAdjacentElement('afterend', this.container)
    }
  }

  renderPackage(pkg) {
    const rates = pkg.shipping_rates || []
    const name = `opal-shipping-rate-${pkg.package_id}`

    return `
      <ul class="opal-shipping-options__list">
        ${rates.map((rate) => this.renderRate(rate, name, pkg.package_id)).join('')}
      </ul>
    `
  }

  renderRate(rate, name, packageId) {
    const isTbd = this.isTbdRate(rate)

    return `
      <li class="opal-shipping-option${rate.selected ? ' is-selected' : ''}">
        <label class="opal-shipping-option__label">
          <input
            class="opal-shipping-option__input"
            type="radio"
            name="${name}"
            value="${this.escape(rate.rate_id)}"
            data-package="${this.escape(String(packageId))}"
            ${rate.selected ? 'checked' : ''}
          >
          <span class="opal-shipping-option__body">
            <span class="opal-shipping-option__name">${this.escape(rate.name)}</span>
            ${this.renderDesc(rate)}
          </span>
          ${isTbd ? '' : `<span class="opal-shipping-option__price">${this.formatPrice(rate)}</span>`}
        </label>
      </li>
    `
  }

  /**
   * Dopisek pokazujemy wyłącznie przy opcji „koszt ustalany indywidualnie”.
   */
  renderDesc(rate) {
    if (!this.isTbdRate(rate)) return ''

    const meta = this.getMeta(rate)
    const note = meta.Informacja || ''
    const tel = meta.Telefon || ''

    if (!note && !tel) return ''

    return `
      <span class="opal-shipping-option__desc">
        ${note ? this.escape(note) : ''}
        ${tel ? `<span class="opal-shipping-option__tel">Tel: <strong>${this.escape(tel)}</strong></span>` : ''}
      </span>
    `
  }

  getMeta(rate) {
    return Object.fromEntries(
      (rate.meta_data || []).map(({ key, value }) => [key, value])
    )
  }

  isTbdRate(rate) {
    return rate.rate_id.endsWith(NO_PRICE_SUFFIX)
  }

  /**
   * Włącza na <body> klasę, gdy wybrana jest opcja o koszcie do ustalenia —
   * CSS podmienia wtedy „Bezpłatnie” na „Do ustalenia” w podsumowaniu.
   */
  syncTbdState(packages) {
    const selected = packages
      .flatMap((pkg) => pkg.shipping_rates || [])
      .find((rate) => rate.selected)

    document.body.classList.toggle(
      TBD_CLASS,
      !!selected && this.isTbdRate(selected)
    )
  }

  formatPrice(rate) {
    const minor = parseInt(rate.price, 10) || 0
    const unit = rate.currency_minor_unit ?? 2
    const value = minor / Math.pow(10, unit)

    if (value === 0) return 'Bezpłatnie'

    let [int, dec] = value.toFixed(unit).split('.')

    if (rate.currency_thousand_separator) {
      int = int.replace(/\B(?=(\d{3})+(?!\d))/g, rate.currency_thousand_separator)
    }

    const amount = dec ? `${int}${rate.currency_decimal_separator}${dec}` : int

    return `${rate.currency_prefix || ''}${amount}${rate.currency_suffix || ''}`
  }

  async onChange(e) {
    const input = e.target.closest('.opal-shipping-option__input')
    if (!input) return

    this.container.classList.add('is-loading')

    try {
      await wp.data
        .dispatch(STORE)
        .selectShippingRate(input.value, input.dataset.package)
    } finally {
      this.container?.classList.remove('is-loading')
    }
  }

  escape(value) {
    const el = document.createElement('div')
    el.textContent = String(value ?? '')
    return el.innerHTML
  }

  destroy() {
    this.container?.remove()
    this.container = null
  }
}
